'use client'

import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { decrypt, encrypt } from '../crypto/crypto'

export type Steps = Record<string, any>

export type StepsState = {
    steps: Steps
    _hydrated: boolean
}

export type StepsAction = {
    addSteps: (steps: Steps) => void
    resetSteps: () => void
    setHasHydrated: (hydrationState: boolean) => void
}

export type StepsStore = StepsState & StepsAction

export const useStepsStore = create<StepsStore>()(
    persist(
        (set) => ({
            steps: {},
            _hydrated: false,
            setHasHydrated: (hydrationState: boolean) => {
                set({
                    _hydrated: hydrationState,
                })
            },
            addSteps: (steps: Steps) => set((state: StepsState) => ({ steps: { ...state.steps, ...steps } })),
            resetSteps: () => set({ steps: {} }),
        }),
        {
            name: 'steps-store',
            storage: createJSONStorage(() => ({
                getItem: async (key) => {
                    let encryptedData: string | null = null
                    try {
                        encryptedData = localStorage.getItem(key)
                    } catch (error) {
                        console.error('localStorage.getItem failed:', error)
                        return null
                    }
                    if (!encryptedData) return null
                    try {
                        const decryptedData = await decrypt(encryptedData)
                        return decryptedData ? JSON.parse(decryptedData) : null
                    } catch (error) {
                        console.error('Decryption failed:', error)
                        return null
                    }
                },
                setItem: async (key, value) => {
                    const encrypted = await encrypt(JSON.stringify(value))
                    if (encrypted) localStorage.setItem(key, encrypted)
                },
                removeItem: (key) => {
                    localStorage.removeItem(key)
                },
            })),
            onRehydrateStorage: () => (state) => {
                state?.setHasHydrated(true)
            },
        }
    )
)
